import { WebViewerInstance } from '@pdftron/webviewer';
import { useEffect, useRef, useState } from 'react';
import { useMutation } from 'react-relay';
import { MutationParameters, PayloadError } from 'relay-runtime';

import { DrawingSheetOrDocumentIdDataType } from '../../../../../service/drawings/src/ui/types/types';
import {
    useDrawingSetAnnotation
} from '../../../../../service/drawings/src/ui/hooks/use-loader-drawing-set-annotation';
import { storeDrawingsModel } from '../../../../../service/drawings/src/ui';
import { DEBOUNCE_DELAY } from '../../../../../shared/const';
import { useAccess } from '@smx-ui-permission/hook/use-access';

let bufferUpdateValue = 0;

export const useAnnotationViewerAppryze = (webViewer: WebViewerInstance | null, sheetData?: DrawingSheetOrDocumentIdDataType) => {
    const [currentDrawingSheetOrDocumentId, setCurrentDrawingSheetOrDocumentId] = useState<string>();
    const [updateValue, setUpdateValue] = useState<number>(bufferUpdateValue);
    const [openedFileName, setOpenedFileName] = useState<string>('');
    const [internalAnnotationList, setInternalAnnotationList] = useState<string>('');

    const annotationList = useDrawingSetAnnotation(updateValue, currentDrawingSheetOrDocumentId);
    const timerRef = useRef<NodeJS.Timeout>(null);
    // const internalWebViewer = useRef<WebViewerInstance>(null);

    const model = storeDrawingsModel.drawingSetAnnotation;

    const [commitAnnotationCreate] = useMutation(model.mutation!.create!);
    const [commitAnnotationUpdate] = useMutation(model.mutation!.update!);
    const [commitAnnotationDelete] = useMutation(model.mutation!.delete!);

    useEffect(() => {
        const {filename} = webViewer?.Core.documentViewer.getDocument() as any || {};
        setOpenedFileName(filename)
    }, [webViewer]);

    useEffect(() => {
        bufferUpdateValue = updateValue + 1;
        setUpdateValue(bufferUpdateValue);
    }, [sheetData?.sheetId, sheetData?.documentId, openedFileName]);

    useEffect(() => {
        if(internalAnnotationList){
            const listTextAnnotation = annotationList.map((annotation) => annotation.node.xfdf);
            const textAnnotation = listTextAnnotation.join();
            const textXfdf = `<?xml version="1.0" encoding="UTF-8" ?><xfdf xmlns="http://ns.adobe.com/xfdf/" xml:space="preserve"><annots>${textAnnotation}</annots></xfdf>`;

            webViewer?.Core.annotationManager.importAnnotations(textXfdf).catch(() => {});
        }
    }, [internalAnnotationList]);

    useEffect(() => {
        setInternalAnnotationList(annotationList.map((annotation) => annotation.cursor).join());
    }, [annotationList]);

    const commitDeleteAnnotationModel = (id: string): MutationParameters => {
        return {
            variables: {
                id,
                connections: [],
            },
            onCompleted: (response: any, errors: PayloadError[]) => {
                if (errors) {
                    console.log(
                        (
                            errors[0] as PayloadError & {
                                extensions: { debugMessage: string };
                            }
                        ).extensions.debugMessage,
                        errors,
                    );
                } else {
                    console.log('Annotation delete successful', response);
                }
            },
            onError: (error: any) => {
                console.error('Mutation error:', error);
            },
        } as unknown as MutationParameters;
    };

    const commitAnnotationModel = (data: any, id?: string): MutationParameters => {
        const newInput = {
            ...data,
        };

        return {
            variables: {
                id,
                input: newInput,
                connections: [],
            },
            onCompleted: (response: any, errors: PayloadError[]) => {
                if (errors) {
                    console.log(
                        (
                            errors[0] as PayloadError & {
                                extensions: { debugMessage: string };
                            }
                        ).extensions.debugMessage,
                        errors,
                    );
                } else {
                    console.log('Annotation save successful', response);
                }
            },
            onError: (error: any) => {
                console.error('Mutation error:', error);
            },
        } as unknown as MutationParameters;
    };

    const accessSaving = useAccess(['drawing', 'documents.edit', 'documents.manage', 'documents.project.edit', 'documents.project.manage']);

    const setupAnnotationInterception = (instance: WebViewerInstance) => {
        const { Core } = instance;
        const annotManager = Core.annotationManager;

        const onAnnotationChanged = async (
            // @ts-expect-error ignore
            annotations: Core.Annotations.Annotation[],
            action: 'add' | 'modify' | 'delete',
            info: { imported?: boolean } = {},
        ) => {
            if (
                !['add', 'modify', 'delete'].includes(action) ||
                info?.imported ||
                annotations[0].Subject === 'Annotation' && !accessSaving
            ) {
                return;
            }

            for (const annot of annotations) {
                try {
                    const annotationXfdf =
                        (
                            await annotManager.exportAnnotations({
                                annotList: [annot],
                                links: false,
                                widgets: false,
                            })
                        ).match(/<annots>(.*?)<\/annots>/s)?.[1] || '';

                    const [bodyAnnotation] =
                    annotationXfdf.match(/<([A-Za-z][\w:-]*)\b[^>]*(?:\/>|>[\s\S]*?<\/\1>)/g) || [];

                    const payload = {
                        id: annot.Id,
                        type: annot.Subject || annot.SubjectName || annot.getType?.() || 'Annotation',
                        drawingSetId: sheetData?.drawingSetId || sheetData?.documentId,
                        drawingSheetId: sheetData?.sheetId || sheetData?.documentId,
                        xfdf: bodyAnnotation,
                    };

                    if(timerRef.current) {
                        clearTimeout(timerRef.current);
                    }

                    timerRef.current = setTimeout(() => {
                        switch (action) {
                            case 'add':
                                commitAnnotationCreate(commitAnnotationModel(payload, undefined));
                                break;
                            case 'modify':
                                delete payload.id;
                                commitAnnotationUpdate(commitAnnotationModel(payload, annot.Id));
                                break;
                            case 'delete':
                                commitAnnotationDelete(commitDeleteAnnotationModel(annot.Id));
                                break;
                            default:
                        }
                    }, DEBOUNCE_DELAY);
                } catch (e) {
                    // console.error('Failed to export/save annotation', e);
                }
            }
        };

        annotManager.addEventListener('annotationChanged', onAnnotationChanged);

        return () => {
            annotManager.removeEventListener('annotationChanged', onAnnotationChanged);
        };
    };

    return {
        setCurrentDrawingSheetOrDocumentId,
        setupAnnotationInterception
    };
};
